/**
 * Evaluation module theme — aliases app brand (Patient indigo SoT).
 *
 * Prefer importing from here in Evaluation UI so the module stays swappable
 * without reaching into Patients/. Color changes still flow from patientTheme.
 */
import { BRAND_THEME, BRAND_THEME_HEX } from "../../ui/brandTheme";

export const EVALUATION_THEME = BRAND_THEME;
export const EVALUATION_THEME_HEX = BRAND_THEME_HEX;

/** Shared position / type chip — one brand tone. */
export const EVALUATION_ROLE_CHIP =
  "bg-indigo-50 text-indigo-700 border border-indigo-100";

/** Role-select card icon tile — unified indigo (no per-role rainbow). */
export const EVALUATION_ROLE_ICON =
  "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100";

export const EVALUATION_BTN = {
  soft: `inline-flex items-center justify-center gap-1 h-6 px-2 text-[11px] font-medium rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.soft}`,
  outline: `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border transition-colors disabled:opacity-50 ${BRAND_THEME.button.outline}`,
  primary: `inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 ${BRAND_THEME.button.primary}`,
  danger:
    "inline-flex items-center justify-center gap-1 h-6 px-2 text-[11px] font-medium rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
  clear:
    "inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 rounded-md border border-indigo-200 transition-colors whitespace-nowrap",
};

export const EVALUATION_FIELD = {
  search: `w-full h-8 pl-9 pr-8 text-sm bg-white border border-indigo-200 rounded-md text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${BRAND_THEME.fieldFocus}`,
  select: `appearance-none h-8 min-w-[120px] pl-3 pr-8 text-sm rounded-md border border-indigo-200 text-slate-700 bg-white outline-none cursor-pointer transition-colors ${BRAND_THEME.fieldFocus}`,
  selectActive:
    "border-indigo-400 text-indigo-700 font-medium bg-indigo-50/60",
};

/**
 * Evaluation form chrome (basic-info step + printed evaluation shell).
 * Headings stay slate (black) — only the frame, icons and accents are indigo.
 */
export const EVALUATION_FORM = {
  shell:
    "max-w-4xl mx-auto bg-white rounded-xl border border-indigo-200 shadow-sm p-6 sm:p-8",
  pageTitle: "text-2xl font-bold text-slate-900 tracking-tight",
  pageSubtitle: "text-sm text-slate-500 mt-1",
  card: "p-5 rounded-xl border border-indigo-200 bg-indigo-50/30",
  cardTitle:
    "text-base font-semibold text-slate-900 mb-4 flex items-center gap-2",
  cardIcon: "text-indigo-500",
  metaLabel:
    "text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1 flex items-center gap-1",
  metaValue: "text-sm font-semibold text-slate-800 mt-1",
  metaGrid:
    "grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 p-4 rounded-lg border border-indigo-200 bg-indigo-50/30",
  panel: "p-6 rounded-lg border border-indigo-200 bg-indigo-50/30",
  panelTitle: "text-base font-semibold text-slate-900 mb-3",
  avatar:
    "w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm",
  footerRow:
    "flex justify-end gap-2 pt-6 mt-6 border-t border-indigo-100",
};

/**
 * History table widths for `table-fixed`.
 * Sr. No. is off; percentages should sum ~100%.
 * Avoid large pl-* on the first column (that caused empty left gap).
 */
export const EVALUATION_COL = {
  type: "w-[8%] min-w-0 pl-3 pr-3 align-middle",
  employee: "w-[26%] min-w-0 pl-3 pr-3 align-middle",
  position: "w-[24%] min-w-0 px-3 align-middle",
  date: "w-[14%] whitespace-nowrap px-3 align-middle tabular-nums",
  status: "w-[12%] whitespace-nowrap px-3 align-middle",
  actions: "w-[16%] whitespace-nowrap pl-2 pr-3 text-right align-middle",
};
